/* ── Enhanced feedback: rating + optional text comment ─────────
   Thumbs up/down as the quick signal, then a text field expands
   on either rating for optional written feedback.
   ─────────────────────────────────────────────────────────────── */

import { useState, useRef, useEffect } from 'react';
import { submitFeedback } from '../api';
import { useToast } from '../contexts/ToastContext';

interface Props {
  queryId: string;
}

export default function FeedbackBar({ queryId }: Props) {
  const [rating, setRating] = useState<'up' | 'down' | null>(null);
  const [showText, setShowText] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const { addToast } = useToast();

  // Auto-focus the textarea when it appears
  useEffect(() => {
    if (showText && textRef.current) {
      textRef.current.focus();
    }
  }, [showText]);

  const handleRate = (r: 'up' | 'down') => {
    if (done || submitting) return;
    setRating(r);
    setShowText(true);
  };

  const handleSend = async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    try {
      await submitFeedback(queryId, rating, comment.trim() || undefined);
    } catch {
      // Silent — feedback UI should never break the experience
    } finally {
      setDone(true);
      setSubmitting(false);
      setShowText(false);
      addToast('success', 'Feedback sent', 2000);
    }
  };

  const handleSkip = async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    try {
      await submitFeedback(queryId, rating);
    } catch {
      // Silent
    } finally {
      setDone(true);
      setSubmitting(false);
      setShowText(false);
    }
  };

  const prompt = rating === 'up'
    ? 'What was helpful about this answer?'
    : 'What was wrong or missing?';

  return (
    <div className="feedback-bar" role="group" aria-label="Response feedback">
      <div className="feedback-buttons">
        <button
          className={`feedback-btn${rating === 'up' ? ' feedback-btn--selected-up' : ''}`}
          onClick={() => handleRate('up')}
          disabled={done || submitting}
          aria-label="Helpful"
          aria-pressed={rating === 'up'}
          title="Helpful"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
        </button>
        <button
          className={`feedback-btn${rating === 'down' ? ' feedback-btn--selected-down' : ''}`}
          onClick={() => handleRate('down')}
          disabled={done || submitting}
          aria-label="Not helpful"
          aria-pressed={rating === 'down'}
          title="Not helpful"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"/></svg>
        </button>
        {done && (
          <span className="feedback-thanks" role="status">
            Thanks — feedback recorded
          </span>
        )}
      </div>

      {showText && !done && (
        <div className="feedback-text-row">
          <textarea
            ref={textRef}
            className="feedback-textarea"
            placeholder={prompt}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
              if (e.key === 'Escape') handleSkip();
            }}
            maxLength={2000}
            rows={2}
            aria-label="Feedback details"
          />
          <div className="feedback-text-actions">
            <button
              className="feedback-send-btn"
              onClick={handleSend}
              disabled={submitting}
            >
              {submitting ? 'Sending…' : 'Send'}
            </button>
            <button
              className="feedback-skip-btn"
              onClick={handleSkip}
              disabled={submitting}
            >
              Skip
            </button>
            <span className="feedback-hint">
              {comment.length > 0 ? `${comment.length}/2000` : 'Optional — Enter to send, Esc to skip'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
